import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";

import { PageHero, Section } from "@/components/site/section";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  subject: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Required").max(2000),
});

export const Route = createFileRoute("/contact-us")({
  head: () => ({
    meta: [
      { title: "Contact Us | Argentinian.Soccer" },
      { name: "description", content: "Get in touch with the Argentinian.Soccer editorial team, or explore jobs and partnerships." },
      { property: "og:title", content: "Contact Us | Argentinian.Soccer" },
      { property: "og:url", content: "/contact-us" },
    ],
  }),
  component: ContactLayout,
});

function ContactLayout() {
  const { location } = useRouterState();
  const isIndex = location.pathname === "/contact-us" || location.pathname === "/contact-us/";
  return isIndex ? <ContactIndex /> : <Outlet />;
}

function ContactIndex() {
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = contactSchema.safeParse(Object.fromEntries(fd));
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      subject: parsed.data.subject || null,
      message: parsed.data.message,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Message sent. We'll get back to you.");
    e.currentTarget.reset();
  }

  return (
    <>
      <PageHero eyebrow="Contact" title="Contact Us" description="Editorial tips, partnerships and jobs." />
      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-xl font-bold text-navy">Send us a message</h2>
            <Input label="Name" name="name" required />
            <Input label="Email" name="email" type="email" required />
            <Input label="Subject" name="subject" />
            <div>
              <label className="mb-1 block text-sm font-medium">Message</label>
              <textarea name="message" rows={6} required className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>

          <div className="space-y-4">
            <Link to="/contact-us/jobs" className="block rounded-lg border border-border bg-card p-5 hover:border-primary">
              <p className="eyebrow">Careers</p>
              <h3 className="mt-1 font-display text-xl font-bold text-navy">Job Listings</h3>
              <p className="mt-1 text-sm text-muted-foreground">Open roles at Argentinian.Soccer.</p>
            </Link>
            <Link to="/contact-us/partnership" className="block rounded-lg border border-border bg-card p-5 hover:border-primary">
              <p className="eyebrow">Partnerships</p>
              <h3 className="mt-1 font-display text-xl font-bold text-navy">Become Our Partner</h3>
              <p className="mt-1 text-sm text-muted-foreground">Sponsorships, integrations, syndication.</p>
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}

function Input({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <input {...props} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
    </div>
  );
}
